import { primitive } from "@primeuix/themes/aura/base";

const theme = {
    primitive: {
        gray: {
            0: '#ffffff',
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cad5e2',
            400: '#90a1b9',
            500: '#62748e',
            600: '#45556c',
            700: '#314158',
            800: '#141e35',
            900: '#0b1325',
        },
        blue: {
            0: '#f5faff',
            50: '#e3f0ff',
            100: '#c9e0ff',
            200: '#a0caff',
            300: '#78b3ff',
            400: '#509cff',
            500: '#2976ff',
            600: '#1c54b2',
            700: '#153e7a',
            800: '#0d274d',
            900: '#08152a'
        },
        green: {
            0: '#f6fff7',
            50: '#e3fbe6',
            100: '#c6f6d5',
            200: '#9ae6b4',
            300: '#68d391',
            400: '#48bb78',
            500: '#38a169',
            600: '#25855a',
            700: '#276749',
            800: '#22543d',
            900: '#1c4532'
        },
        orange: {
            0: '#fffaf5',
            50: '#fff3e0',
            100: '#ffe0b2',
            200: '#ffcc80',
            300: '#ffb74d',
            400: '#ffa726',
            500: '#ff9800',
            600: '#fb8c00',
            700: '#f57c00',
            800: '#ef6c00',
            900: '#e65100'
        },
        yellow: {
            0: '#fffcf5',
            50: '#fffde7',
            100: '#fff9c4',
            200: '#fff59d',
            300: '#fff176',
            400: '#ffee58',
            500: '#ffeb3b',
            600: '#fdd835',
            700: '#fbc02d',
            800: '#f9a825',
            900: '#f57f17'
        },
        zinc: {
            0: '#ffffff',
            50: '#f7f7f7',
            100: '#ededed',
            200: '#d6d6d6',
            300: '#bdbdbd',
            400: '#a3a3a3',
            500: '#8a8a8a',
            600: '#707070',
            700: '#575757',
            800: '#3d3d3d',
            900: '#232323',
            950: '#000000'
        },
        red: {
            0: '#fff5f5',
            50: '#fef2f2',
            100: '#fee2e2',
            200: '#fecaca',
            300: '#fca5a5',
            400: '#f87171',
            500: '#ef4444',
            600: '#dc2626',
            700: '#b91c1c',
            800: '#991b1b',
            900: '#7f1d1d',
            950: '#450a0a'
        },
        purple: {
            0: '#ffffff',
            50: '#f7f7f7',
            100: '#ededed',
            200: '#d6d6d6',
            300: '#bdbdbd',
            400: '#a3a3a3',
            500: '#8a8a8a',
            600: '#707070',
            700: '#575757',
            800: '#3d3d3d',
            900: '#232323',
            950: '#000000'
        },
        lime: {
            50: '#f7f7f7',
            100: '#ededed',
            200: '#d6d6d6',
            300: '#bdbdbd',
            400: '#a3a3a3',
            500: '#8a8a8a',
            600: '#707070',
            700: '#575757',
            800: '#3d3d3d',
            900: '#232323',
            950: '#000000'
        }
    },
    semantic: {
        primary: {
            0: '{blue.50}',
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        },
        colorScheme: {
            light: {
                surface: {
                    0: '{gray.0}',
                    50: '{gray.50}',
                    100: '{gray.100}',
                    200: '{gray.200}',
                    300: '{gray.300}',
                    400: '{gray.400}',
                    500: '{gray.500}',
                    600: '{gray.600}',
                    700: '{gray.700}',
                    800: '{gray.800}',
                    900: '{gray.900}'
                },
                primary: {
                    color: '{blue.200}',
                    contrastColor: '{blue.700}',
                    hoverColor: '{blue.300}',
                    activeColor: '{blue.300}',
                },
                formField: {
                    borderColor: '{surface.400}',
                    disabledBackground: '{surface.100}',
                    focusBorderColor: '{surface.200}',
                },
                content: {
                    borderColor: '{surface.300}',
                },
                highlight: {
                    background: '{surface.50}',
                    focusBackground: '{surface.100}',
                    color: '{surface.600}',
                    focusColor: '{surface.600}', 
                }
            },
            dark: {
                surface: {
                    0: '{zinc.800}',
                    50: '{zinc.900}',
                    100: '{zinc.900}',
                    200: '{zinc.800}',
                    300: '{zinc.700}',
                    400: '{zinc.600}',
                    500: '{zinc.500}',
                    600: '{zinc.400}',
                    700: '{zinc.300}',
                    800: '{zinc.200}',
                    900: '{zinc.100}',
                },
                primary: {
                    color: '{blue.200}',
                    contrastColor: '{blue.700}',
                    hoverColor: '{blue.300}',
                    activeColor: '{blue.300}',
                },
                formField: {
                    borderColor: '{surface.400}',
                    disabledBackground: '{zinc.100}',
                    focusBorderColor: '{surface.500}',
                },
                content: {
                    borderColor: '{zinc.300}',
                },
                highlight: {
                    background: '{zinc.50}',
                    focusBackground: '{zinc.100}',
                    color: '{zinc.600}',
                    focusColor: '{zinc.600}', 
                }
            }
        }
    },
    components: {
        menubar: {
            background: '{surface.0}',
            item: {
                focus: {
                    background: '{surface.50}',
                    color: '{surface.900}',
                },
                active: {
                    background: '{surface.50}',
                    color: '{surface.900}',
                }
            },
            submenu: {
                background: '{surface.0}',
                border: {
                    color: '{surface.300}',
                },
                item: {
                    focus: {
                        background: '{surface.50}',
                    }
                }
            }
        },
        menu: {
            background: '{surface.0}',
            color: '{surface.900}',
            border: {
                color: '{surface.300}',
            },
            item: {
                color: '{surface.900}',
                focus: {
                    background: '{surface.50}',
                    color: '{surface.900}',
                },
            },
            submenu: {
                background: '{surface.0}',
                border: {
                    color: '{surface.300}',
                },
                item: {
                    focus: {
                        background: '{surface.50}',
                    }
                }
            }
        },
        button: {
            label: {
                font:  {
                    weight: 'medium',
                }
            },
            text: {
                secondary: {
                    color: '{surface.950}',
                    background: '{surface.50}',
                    hover: {
                        background: '{surface.100}!important',
                    },
                    active: {
                        background: '{surface.100}',
                    },
                    focus: {
                        background: '{surface.100}',
                    }
                },
                info: {
                    color: '{blue.600}',
                    background: '{blue.0}',
                    hover: {
                        background: 'transparent!important',
                        color: '{blue.400}!important',
                    },
                    active: {
                        background: '{blue.100}',
                    },
                    focus: {
                        background: '{blue.100}',
                    }
                },
            },
            success: {
                color: '{surface.0}!important',
                background: '{green.400}!important',
                border: {
                    color: '{green.400}!important',
                },
                hover: {
                    background: '{green.500}!important',
                    color: '{surface.0}!important',
                    border: {
                        color: '{green.500}!important',
                    },
                },
                focus: {
                    ring: {
                        color: '{green.500}',
                        shadow: '{green.500}',
                    }
                }
            },
            secondary: {
                color: '{surface.900}',
                background: '{surface.100}!important',
                border: {
                    color: '{surface.100}!important',
                },
                hover: {
                    background: '{surface.100}!important',
                    color: '{surface.900}!important',
                    border: {
                        color: '{surface.100}!important',
                    },
                },
                focus: {
                    ring: {
                        color: '{surface.200}',
                        shadow: '{surface.200}',
                    }
                }
            },
            info: {
                color: '{blue.0}!important',
                background: '{blue.500}!important',
                border: {
                    color: '{blue.500}!important',
                },
                hover: {
                    background: '{blue.600}!important',
                    color: '{blue.0}!important',
                    border: {
                        color: '{blue.600}!important',
                    },
                },
                focus: {
                    ring: {
                        color: '{blue.200}',
                        shadow: '{blue.200}',
                    }
                }
            },
        },
        skeleton: {
            background: '{surface.50}',
            animation: {
                background: '{surface.0}',
            }
        },
        toggleswitch: {
            background: '{surface.300}!important',
            hover: {
                background: '{surface.400}!important',
            },
            checked: {
                background: '{green.100}!important',
            },
            handle: {
                background: '{surface.0}!important',
                checked: {
                    background: '{surface.0}!important',
                    hover: {
                        background: '{surface.0}!important',
                    }
                },
                hover: {
                    background: '{surface.0}!important',
                }
            }
        },
        message: {
            warn: {
                color: '{orange.600}!important',
                border: {
                    color: '{yellow.500}!important',
                },
                outlined: {
                    border: {
                        color: '{yellow.500}',
                    }
                },
                simple: {
                    color: '{yellow.500}!important',
                }
            },
            success: {
                color: '{green.400}!important',
                border: {
                    color: '{green.500}',
                },
                outlined: {
                    border: {
                        color: '{green.500}',
                    }
                },
                simple: {
                    color: '{green.500}',
                }
            },
            danger: {
                color: '{red.400}!important',
                border: {
                    color: '{red.500}',
                },
                outlined: {
                    border: {
                        color: '{red.500}',
                    }
                },
                simple: {
                    color: '{red.500}',
                }
            },
            info: {
                color: '{blue.400}!important',
                border: {
                    color: '{blue.500}',
                },
                outlined: {
                    border: {
                        color: '{blue.500}',
                    }
                },
                simple: {
                    color: '{blue.500}',
                }
            },
        },
        inputtext: {
            background: '{surface.0}',
            color: '{surface.900}!important',
            disabled: {
                background: '{surface.100}',
            }
        },
        inputnumber: {
            background: '{surface.0}',
            color: '{surface.900}!important',
            disabled: {
                background: '{surface.100}',
            }
        },
        inputgroup: {
            addon: {
                background: '{surface.0}!important',
                border: {
                    color: '{surface.400}',
                }
            }
        },
        select: {
            background: '{surface.0}',
            color: '{surface.900}',
            overlay: {
                background: '{surface.0}',
                border: {
                    color: '{surface.300}',
                }
            },
            option: {
                color: '{surface.900}',
                focus: {
                    background: '{surface.50}',
                    color: '{surface.900}',
                },
                selected: {
                    background: '{surface.100}',
                    focus: {
                        background: '{surface.100}',
                        color: '{surface.900}',
                    },
                    color: '{surface.900}',
                }
            },
            disabled: {
                background: '{surface.100}',
            },
            lg: {
                padding: {
                    x: '1rem',
                    y: '0.5rem',
                }
            },
            transition: {
                duration: '1ms',
            }
        },
        dialog: {
            background: '{surface.0}',
            color: '{surface.900}',
            border: {
                color: '{surface.300}',
            }
        },
        avatar: {
            background: '{surface.100}!important',
            color: '{surface.900}',
            group: {
                border: {
                    color: '{surface.0}',
                }
            }
        },
        textarea: {
            background: '{surface.0}',
            color: '{surface.900}',
            border: {
                color: '{surface.300}',
            },
            filled: {
                background: '{surface.50}',
                color: '{surface.900}',
            },
            disabled: {
                background: '{surface.100}',
                color: '{surface.600}',
            }
        },
        accordion: {
            panel: {
                border: {
                    color: '{surface.300}',
                }
            },
            header: {
                background: '{surface.0}',
                color: '{surface.700}',
                border: {
                    color: '{surface.300}',
                },
                hover: {
                    background: '{surface.50}',
                    color: '{surface.900}',
                },
                active: {
                    background: '{surface.0}',
                    color: '{surface.800}',
                    hover: {
                        background: '{surface.0}',
                    }
                }
            },
            content: {
                background: '{surface.0}',
                color: '{surface.600}',
            }
        },
        progressbar: {
            background: '{surface.100}',
            value: {
                background: '{blue.500}',
            }
        },
        toast: {
            border: {
                radius: '1rem',
            }
        },
        fileupload:{
            background: '{surface.0}',
            border: {
                color: '{surface.200}',
            },
        },
        datatable: {
            border: {
                color: '{surface.100}',
            },
            header: {
                background: '{surface.0}',
                color: '{surface.800}',
                cell: {
                    background: '{surface.0}!important',
                    color: '{surface.800}!important',
                    border: {
                        color: '{surface.100}!important',
                    },
                },
                border: {
                    color: '{surface.100}',
                },
            },
            body: {
                background: '{surface.0}',
                color: '{surface.800}',
                cell: {
                    background: '{surface.0}!important',
                    color: '{surface.800}!important',
                    border: {
                        color: '{surface.100}',
                    },
                },
                border: {
                    color: '{surface.100}',
                },
            },
            row: {
                background: '{surface.0}',
                color: '{surface.900}',
            },
            footer: {
                background: '{surface.0}',
                color: '{surface.900}',
                border: {
                    color: '{surface.100}!important',
                },
                cell: {
                    background: '{surface.0}',
                    border: {
                        color: '{surface.100}!important',
                    },
                }
            },
        },
        paginator: {
            background: '{surface.0}',
            color: '{surface.900}',
            nav: {
                button: {
                    background: '{surface.0}',
                    color: '{surface.900}',
                    hover: {
                        background: '{surface.100}',
                    },
                    selected: {
                        background: '{surface.100}',
                    }
                }
            }
        },
        divider: {
            border: {
                color: '{surface.300}',
            },
            content: {
                background: '{surface.0}',
            }
        },
        card: {
            background: '{surface.0}',
            color: '{surface.900}',
            border: {
                color: '{surface.300}',
            }
        },
        tabs: {
            tab: {
                background: '{surface.0}',
                active: {
                    color: '{blue.500}',
                    border: {
                        color: '{blue.500}!important',
                    }
                }
            },
            tablist: {
                background: '{surface.0}',
            },
            tabpanel: {
                background: '{surface.0}',
                color: '{surface.900}',
            },
            active: {
                bar: {
                    background: '{blue.500}',
                    height: '2px',
                }
            }
        },
        toast: {
            border: {
                radius: '1rem',
            },
            info: {
                background: '{blue.50}',
                color: '{blue.400}!important',
                detail: {
                    color: '{surface.700}!important',
                }
            },
            success: {
                background: '{green.50}',
                color: '{green.400}!important',
                detail: {
                    color: '{surface.700}!important',
                }
            }
        },
        tooltip: {
            background: '{surface.100}',
            color: '{surface.900}',
        },
        tag: {
            font: {
                weight: 'medium',
            }
        },
        message: {
            secondary: {
                background: '{surface.100}!important',
                border: {
                    color: '{surface.200}!important',
                }
            },
        },
        selectbutton: {
            border: {
                radius: '10rem!important',
            }
        },
        togglebutton: {
            background: '{surface.100}!important',
            border: {
                radius: '10rem',
                color: '{surface.100}!important',
            },
            checked: {
                background: '{surface.100}!important',
                border: {
                    color: '{surface.100}!important',
                },
            },
            content: {
                checked: {
                    background: '{surface.0}!important',
                },
            },
            hover: {
                background: '{surface.100}!important',
                border: {
                    color: '{surface.100}!important',
                },
            }
        },
        progressbar: {
            label: {
                color: '{surface.0}',
            },
            background: '{surface.100}',
            value: {
                background: '{blue.400}',
            }
        }
    }
}

export default theme;